import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Loader2, Lightbulb } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CatalogProposalsTab({ language }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/catalog-proposals`, {
        params: filter !== 'all' ? { status: filter } : {},
      });
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/admin/catalog-proposals/${id}`, { status });
      await load();
    } catch (e) {
      alert(formatApiError(e, language === 'lv' ? 'Kļūda' : 'Error'));
    } finally {
      setBusyId(null);
    }
  };

  const STATUS_LABEL = {
    lv: { pending: 'Gaida', approved: 'Apstiprināts', rejected: 'Noraidīts', all: 'Visi' },
    en: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', all: 'All' },
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="admin-proposals-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <Lightbulb className="w-6 h-6 text-[#D97736]" />
          <h2 className="font-outfit text-xl font-bold text-[#121212]">
            {language === 'lv' ? 'Kataloga ierosinājumi' : 'Catalog Proposals'}
          </h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                filter === f ? 'bg-[#D97736] text-white' : 'border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736]'
              }`}
              data-testid={`proposals-filter-${f}`}
            >
              {STATUS_LABEL[language]?.[f] || f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#D97736]" /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#5C5C5C] py-12 text-sm">
          {language === 'lv' ? 'Šajā kategorijā nav ierosinājumu.' : 'No proposals in this category.'}
        </p>
      ) : (
        <div className="space-y-3">
          {items.map(p => (
            <div key={p.id} className="p-4 rounded-xl border border-[#E5E4E0]" data-testid={`proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-[#121212]">{p.title}</h3>
                  <p className="text-xs text-[#5C5C5C] mt-0.5">
                    {p.shop_name ? `${p.shop_name} · ` : ''}{p.user_email}
                    {p.season_key && <span> · {p.season_key}</span>}
                  </p>
                </div>
                <Badge variant="outline" className={`text-xs ${
                  p.status === 'approved' ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                  : p.status === 'rejected' ? 'border-red-300 text-red-700 bg-red-50'
                  : 'border-amber-300 text-amber-700 bg-amber-50'
                }`}>
                  {STATUS_LABEL[language]?.[p.status] || p.status}
                </Badge>
              </div>
              {p.description && <p className="text-sm text-[#5C5C5C] mt-2 whitespace-pre-line">{p.description}</p>}
              {p.reason && (
                <p className="text-xs text-[#8A8A8A] mt-2 italic">
                  {language === 'lv' ? 'Kāpēc: ' : 'Why: '}{p.reason}
                </p>
              )}
              {p.status === 'pending' && (
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => setStatus(p.id, 'approved')}
                    disabled={busyId === p.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`proposal-approve-${p.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {language === 'lv' ? 'Apstiprināt' : 'Approve'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStatus(p.id, 'rejected')}
                    disabled={busyId === p.id}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`proposal-reject-${p.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    {language === 'lv' ? 'Noraidīt' : 'Reject'}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CatalogProposalsTab;
